import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import MainNavigation from '../../components/ui/MainNavigation';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TrailerPhotoCapture from './components/TrailerPhotoCapture';
import VerificationSelfie from './components/VerificationSelfie';
import ReviewSubmission from './components/ReviewSubmission';
import checkInService from '../../services/checkInService';

const RenterCheckInProcess = () => {
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams?.get('bookingId');
  const ownerId = searchParams?.get('ownerId');

  const [currentStep, setCurrentStep] = useState(0);
  const [checkInId, setCheckInId] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [gpsLocation, setGpsLocation] = useState(null);
  const [signature, setSignature] = useState(null);

  const [sectionPhotos, setSectionPhotos] = useState({
    exterior: [],
    interior: [],
    hitch: [],
    electrical: [],
    tires: []
  });

  const [sectionNA, setSectionNA] = useState({
    exterior: false,
    interior: false,
    hitch: false,
    electrical: false,
    tires: false
  });

  const [verificationSelfie, setVerificationSelfie] = useState(null);
  const [damageNotes, setDamageNotes] = useState('');

  const inspectionSections = [
    {
      id: 'exterior',
      title: 'Exterior Panels',
      description: 'Document all exterior surfaces, sides, front, and rear',
      requiredPhotos: 4,
      icon: 'Box',
      tips: [
        'Capture all four sides of the trailer',
        'Include close-ups of any scratches or dents',
        'Take photos in good lighting'
      ]
    },
    {
      id: 'interior',
      title: 'Interior Condition',
      description: 'Capture floor, walls, and cargo area condition',
      requiredPhotos: 3,
      icon: 'Home',
      tips: [
        'Document floor condition',
        'Capture wall and ceiling condition',
        'Show cargo area cleanliness'
      ]
    },
    {
      id: 'hitch',
      title: 'Hitch Components',
      description: 'Inspect hitch, coupler, safety chains, and jack',
      requiredPhotos: 2,
      icon: 'Link',
      tips: [
        'Show hitch and coupler clearly',
        'Document safety chains and jack condition'
      ]
    },
    {
      id: 'electrical',
      title: 'Electrical Systems',
      description: 'Check lights, wiring, and electrical connections',
      requiredPhotos: 2,
      icon: 'Zap',
      tips: [
        'Test and photograph all lights working',
        'Document wiring and connector condition'
      ]
    },
    {
      id: 'tires',
      title: 'Tire Condition',
      description: 'Document all tires and wheel condition',
      requiredPhotos: 4,
      icon: 'Circle',
      tips: [
        'Photograph each tire individually',
        'Show tread depth and sidewall condition'
      ]
    }
  ];

  const steps = [
    { title: 'Trailer Photos', icon: 'Camera' },
    { title: 'Verification', icon: 'User' },
    { title: 'Review & Sign', icon: 'PenLine' }
  ];

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login');
    }
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!bookingId || !ownerId) {
      setError('Missing booking information. Please access check-in from your rental.');
      return;
    }

    if (isAuthenticated && user) {
      initializeCheckIn();
    }
  }, [bookingId, ownerId, isAuthenticated, user]);

  useEffect(() => {
    if (navigator?.geolocation) {
      navigator?.geolocation?.getCurrentPosition(
        (position) => {
          setGpsLocation({
            latitude: position?.coords?.latitude,
            longitude: position?.coords?.longitude
          });
        },
        (error) => {
          console.error('GPS error:', error);
        }
      );
    }
  }, []);

  const initializeCheckIn = async () => {
    try {
      const { data: existingCheckIn } = await checkInService?.getCheckInByBookingId(bookingId);
      
      if (existingCheckIn) {
        setCheckInId(existingCheckIn?.id);
        if (existingCheckIn?.status === 'submitted') {
          setError('Check-in already completed for this booking.');
        }
      } else {
        const { data: newCheckIn, error: createError } = await checkInService?.createCheckIn(
          bookingId,
          user?.id,
          ownerId
        );

        if (createError) {
          setError('Failed to initialize check-in. Please try again.');
          return;
        }

        setCheckInId(newCheckIn?.id);

        if (gpsLocation) {
          await checkInService?.updateGpsLocation(
            newCheckIn?.id,
            gpsLocation?.latitude,
            gpsLocation?.longitude
          );
        }
      }
    } catch (error) {
      console.error('Initialize check-in error:', error);
      setError('Failed to initialize check-in.');
    }
  };

  const handleSectionNAChange = (sectionId, isNA) => {
    setSectionNA(prev => ({ ...prev, [sectionId]: isNA }));
  };

  const handleSectionPhotosChange = async (sectionId, photos) => {
    setSectionPhotos(prev => ({ ...prev, [sectionId]: photos }));

    if (checkInId && photos?.length > 0) {
      try {
        const uploadedPhotos = [];
        for (const photo of photos) {
          if (photo?.file && !photo?.uploaded) {
            const { data, error } = await checkInService?.uploadPhoto(
              photo?.file,
              checkInId,
              sectionId
            );
            if (!error && data) {
              uploadedPhotos?.push({ ...photo, path: data?.path, uploaded: true });
            }
          } else {
            uploadedPhotos?.push(photo);
          }
        }

        const photoPaths = uploadedPhotos?.map(p => p?.path)?.filter(Boolean);
        await checkInService?.updateSectionPhotos(checkInId, sectionId, photoPaths);
      } catch (error) {
        console.error('Upload section photos error:', error);
      }
    }
  };

  const handleVerificationSelfieChange = async (selfie) => {
    setVerificationSelfie(selfie);

    if (checkInId && selfie?.file) {
      try {
        const { data, error } = await checkInService?.uploadPhoto(
          selfie?.file,
          checkInId,
          'verification'
        );
        if (!error && data) {
          await checkInService?.updateVerificationSelfie(checkInId, data?.path);
        }
      } catch (error) {
        console.error('Upload verification selfie error:', error);
      }
    }
  };

  const handleSignatureChange = async (signatureData) => {
    setSignature(signatureData);
    if (checkInId && signatureData) {
      try {
        const { supabase } = await import('../../lib/supabase');
        await supabase
          ?.from('renter_check_ins')
          ?.update({ signature_data: signatureData })
          ?.eq('id', checkInId);
      } catch (error) {
        console.error('Save signature error:', error);
      }
    }
  };

  const handleSubmit = async () => {
    if (!checkInId) {
      setError('Check-in not initialized. Please refresh and try again.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (damageNotes) {
        await checkInService?.updateDamageNotes(checkInId, damageNotes);
      }

      await checkInService?.completeCheckIn(checkInId);
      await checkInService?.submitCheckIn(checkInId);
      await checkInService?.notifyOwner(checkInId, ownerId, user?.id, bookingId);

      navigate('/renter-dashboard', { 
        state: { message: 'Check-in submitted successfully! Owner has been notified.' } 
      });
    } catch (error) {
      console.error('Submit check-in error:', error);
      setError('Failed to submit check-in. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getProgressPercentage = () => {
    const totalSections = inspectionSections?.length + 1;
    const completedSections = inspectionSections?.filter(
      section => sectionNA?.[section?.id] || sectionPhotos?.[section?.id]?.length >= section?.requiredPhotos
    )?.length + (verificationSelfie ? 1 : 0);
    return Math.round((completedSections / totalSections) * 100);
  };

  const canProceedToVerification = inspectionSections?.every(
    section => sectionNA?.[section?.id] || sectionPhotos?.[section?.id]?.length >= section?.requiredPhotos
  );

  const canProceedToReview = canProceedToVerification && verificationSelfie;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <MainNavigation isAuthenticated={isAuthenticated} user={user} />
        <div className="pt-20 flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (error && !checkInId) {
    return (
      <div className="min-h-screen bg-background">
        <MainNavigation isAuthenticated={isAuthenticated} user={user} />
        <div className="pt-20 px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <div className="bg-error/10 border border-error/20 rounded-lg p-6 text-center">
              <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" strokeWidth={2} />
              <h2 className="font-heading text-xl font-semibold text-foreground mb-2">
                Unable to Start Check-In
              </h2>
              <p className="font-body text-sm text-muted-foreground mb-4">{error}</p>
              <Button variant="primary" onClick={() => navigate('/renter-dashboard')}>
                Return to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation isAuthenticated={isAuthenticated} user={user} />
      
      <div className="pt-20 px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3 mb-2">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-muted rounded-md transition-smooth"
              >
                <Icon name="ArrowLeft" size={20} strokeWidth={2} />
              </button>
              <div>
                <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground">
                  Renter Check-In Process
                </h1>
                <p className="font-body text-base text-muted-foreground">
                  Document trailer condition before pickup
                </p>
              </div>
            </div>
          </div>

          {/* GPS Status */}
          {gpsLocation && (
            <div className="bg-success/10 border border-success/20 rounded-lg p-3 mb-6 flex items-center space-x-3">
              <Icon name="MapPin" size={18} className="text-success" strokeWidth={2} />
              <p className="font-body text-sm text-success font-medium">
                GPS location verified — Lat: {gpsLocation?.latitude?.toFixed(4)}, Lng: {gpsLocation?.longitude?.toFixed(4)}
              </p>
            </div>
          )}

          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-2">
              <span className="font-body text-sm font-semibold text-foreground">Progress</span>
              <span className="font-body text-sm font-semibold text-primary">{getProgressPercentage()}%</span>
            </div>
            <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary transition-all duration-300"
                style={{ width: `${getProgressPercentage()}%` }}
              />
            </div>
          </div>

          {/* Step Indicators */}
          <div className="flex items-center justify-between mb-8">
            {steps?.map((step, index) => (
              <div key={index} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center mb-2 transition-smooth ${
                      currentStep === index
                        ? 'bg-primary text-primary-foreground'
                        : currentStep > index
                        ? 'bg-success text-success-foreground'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    <Icon name={step?.icon} size={20} strokeWidth={2.5} />
                  </div>
                  <span className="font-body text-xs md:text-sm font-medium text-center">
                    {step?.title}
                  </span>
                </div>
                {index < steps?.length - 1 && (
                  <div
                    className={`h-1 flex-1 mx-2 transition-smooth ${
                      currentStep > index ? 'bg-success' : 'bg-muted'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error/20 rounded-md">
              <div className="flex items-start space-x-3">
                <Icon name="AlertCircle" size={20} className="text-error mt-0.5" strokeWidth={2} />
                <p className="font-body text-sm text-foreground">{error}</p>
              </div>
            </div>
          )}

          {/* Step Content */}
          <div className="bg-card rounded-lg shadow-elevation-3 p-6 mb-6">
            {currentStep === 0 && (
              <div className="space-y-8">
                <div>
                  <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
                    Trailer Condition Documentation
                  </h2>
                  <p className="font-body text-sm text-muted-foreground">
                    Capture photos of all required sections to document pre-rental condition
                  </p>
                </div>
                {inspectionSections?.map((section) => (
                  <TrailerPhotoCapture
                    key={section?.id}
                    section={{ ...section, photos: sectionPhotos?.[section?.id] }}
                    onPhotosChange={(photos) => handleSectionPhotosChange(section?.id, photos)}
                    initialPhotos={sectionPhotos?.[section?.id]}
                    isNA={sectionNA?.[section?.id]}
                    onNAChange={(isNA) => handleSectionNAChange(section?.id, isNA)}
                  />
                ))}
              </div>
            )}

            {currentStep === 1 && (
              <div>
                <h2 className="font-heading text-2xl font-semibold text-foreground mb-6">
                  Identity Verification
                </h2>
                <VerificationSelfie
                  onSelfieChange={handleVerificationSelfieChange}
                  initialSelfie={verificationSelfie}
                />
              </div>
            )}

            {currentStep === 2 && (
              <div>
                <h2 className="font-heading text-2xl font-semibold text-foreground mb-6">
                  Review & Sign
                </h2>
                <ReviewSubmission
                  sections={inspectionSections?.map(section => ({
                    ...section,
                    photos: sectionPhotos?.[section?.id],
                    isNA: sectionNA?.[section?.id]
                  }))}
                  verificationSelfie={verificationSelfie}
                  damageNotes={damageNotes}
                  onDamageNotesChange={setDamageNotes}
                  onSubmit={handleSubmit}
                  isSubmitting={isSubmitting}
                  onSignatureChange={handleSignatureChange}
                  signature={signature}
                />
              </div>
            )}
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => {
                if (currentStep === 0) {
                  navigate(-1);
                } else {
                  setCurrentStep(currentStep - 1);
                }
              }}
              iconName="ArrowLeft"
            >
              {currentStep === 0 ? 'Cancel' : 'Back'}
            </Button>

            {currentStep < 2 && (
              <Button
                variant="primary"
                onClick={() => setCurrentStep(currentStep + 1)}
                disabled={
                  (currentStep === 0 && !canProceedToVerification) ||
                  (currentStep === 1 && !verificationSelfie)
                }
                iconName="ArrowRight"
              >
                Continue
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RenterCheckInProcess;